<template>
    <LoadingView v-if="loading" />
    <div v-if="!loading" class="flex flex-col gap-8">
        <nav class="flex flex-none gap-4 justify-between items-center rounded-t-lg">
            <div class="flex-none">
                <FormButton
                    color="secondary"
                    icon="fa-chevron-left" @click="$router.go(-1)">
                    Back
                </FormButton>
            </div>
            <div class="py-4 sm:rounded-tr-lg flex justify-end items-center h-16 gap-2">
                <FormButton type="button" @click="openDelete()" prefix-class="fak" icon="fa-trash" color="red" />
            </div>
        </nav>

        <header class="flex items-start gap-8">
           <div class="flex flex-col lg:flex-row gap-5 sm:gap-6 flex-1">
                <div class="flex flex-col gap-5 sm:gap-6 flex-1 justify-between">
                    <div class="flex flex-col lg:flex-row gap-5 justify-between flex-none">

                        <div class="flex-1 flex flex-col gap-2 lg:gap-0 order-2 lg:order-1">
                            <h1 class="text-3xl leading-9 font-headline antialiased font-medium bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
                                {{ survey.filename }}
                            </h1>
                            <dl class="lg:flex gap-2">
                                <dt class="sr-only">
                                    Division
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300 pr-2">
                                    {{ survey.division.meta?.info?.name }}
                                </dd>
                            </dl>
                        </div>
                    </div>
                    <section class="flex flex-row w-full gap-2">
                        <h2 class="sr-only col-span-full">Basic Information</h2>
                        <InfoText v-if="survey?.parcels?.length" class="w-full" label="Total Parcel Records" icon="fak fa-list-check" :value="String(survey.parcels.length)" :color="survey.parcels.length > 0 ? 'green' : 'red'" :bg-color="survey.parcels.length > 0 ? 'green' : 'red'" />
                        <InfoText v-if="getUnit" class="w-full" label="Measurement Unit" icon="fak fa-ruler" :value="getUnit" bg-color="gray" />
                        <InfoText v-if="survey?.meta?.filing" class="w-full" label="Filing" icon="fak fa-lot" :value="survey.meta?.filing" bg-color="gray" />
                        <InfoText v-if="getSystem" class="w-full" label="Coordinate System" icon="fak fa-survey" :value="getSystem" bg-color="gray" />
                    </section>

                </div>
                <div class="w-full lg:w-1/5 flex">
                    <div class="text-5xl bg-bhav-blue-500 rounded-lg text-white p-8 flex items-center justify-center w-full">
                        <span class="far fa-file"></span>
                    </div>
                </div>

            </div>
        </header>
        <TabPanes route="survey-tab" :tabs="tabs" v-slot="{tab}" via="via-white" to="to-white" class="gap-4 py-6">
            <ParcelsPane v-if="tab.name === 'parcels'" :jobs="job" :parcels="survey.parcels" @update="checkJobs()"></ParcelsPane>
            <template v-if="tab.name === 'debugger'">
                <h2>Raw Data</h2>
                <pre>{{ xml }}</pre>
            </template>
        </TabPanes>
    </div>

    <ModalDialog v-if="deleting" :no-padding="false" :hide-header="true" :open="deleting" @close="cancel">
        <template #body>
            <h2 class="text-lg text-oakwood-red-600 font-semibold">Confirm Survey Deletion</h2>
            <p class="text-oakwood-gray-900 mb-4"><strong>Warning!</strong> You are about to delete <em>{{ survey.filename }}</em>. 
            </p>
            <p class="mb-4">Deleting this survey will also <strong>delete related parcels</strong> which will also be <strong>unlinked from matching lots.</strong></p>
            <p>Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
            <FormButton type="button" @click="drop" color="blue">
                Delete
            </FormButton>
        </template>
    </ModalDialog>
</template>

<script>
import axios from 'axios';
import LoadingView from '@/components/LoadingView.vue';
import { FormButton } from '@/components/form';
import { systems, measurements, handle404 } from '@/helpers/globals';
import ParcelsPane from '@/components/surveys/ParcelsPane.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import TabPanes from '@/components/TabPanes.vue';
import InfoText from "@/components/media/InfoText.vue";
import { useAuthStore } from '@/stores/auth';
import { mapState } from 'pinia';

let tabs = [
    {
        name: 'parcels',
        icon: 'fa-ruler',
        title: 'Parcels',
        acl: 2
    },
    {
        name: 'debugger',
        icon: 'fa-debug',
        title: 'Debugger',
        acl: 2
    },
];

export default {
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.survey?.filename}`
        }
    },
    data() {
        return {
            loading: true,
            survey: {},
            systems,
            measurements,
            tabs,
            xml: null,
            job: false,
            deleting: false
        }
    },
    methods: {
        show() {
            axios.get(`surveys/${this.$route.params?.id}`)
                .then(resp => {
                    this.survey = resp.data;
                    this.loading = false;
                    this.getXMLFile();
                    if(this.survey.parcels.length == 0) {
                        this.checkJobs();
                    }
                })
                .catch(err => {
                    if(err.response.status === 404)
                        handle404(this, err.response.data.message);
                })
        },
        drop() {
            axios.delete(`surveys/${this.survey.id}`)
            .then(() => {
                this.$router.go(-1);
            })
            .catch(err => {
                console.error(err);
            })
        },
        cancel() {
            this.deleting = false;
        },
        openDelete() {
            this.deleting = true;
        },
        getXMLFile() {
            axios.get(`surveys/${this.$route.params?.id}/file`)
                .then(resp => {
                    this.xml = resp.data
                })
        },
        checkJobs() {
            let check = setInterval(
                () => {
                    this.refresh(check);
                },
                2000
            );
            this.refresh(check);
        },
        refresh(check) {
            axios.get(`surveys/${this.$route.params?.id}/jobs`)
            .then(resp => {
                if(resp.data.length == 0) {
                    clearInterval(check);
                    this.job = false;
                    this.show();
                } else {
                    this.job = true;
                }
            })
        }
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        getSystem() {
            return systems.find(system => system.value == this.survey.meta?.system)?.label || null;
        },
        getUnit() {
            return measurements.find(unit => unit.value == this.survey.meta?.unit)?.label || null;
        }
    },
    created() {
        this.show();
        this.tabs = tabs.filter(tab => {
            return this.user.level >= tab.acl
        });
    },
    components: {
        LoadingView,
        FormButton,
        TabPanes,
        ParcelsPane,
        ModalDialog,
        InfoText
    }

}
</script>